import { Decoder, tools, Reader } from 'ts-ebml';

export default {
  data() {
    return {
      REC_stream: null,
      REC_userMediaSupported: null,
      REC_cameraPermissions: null,
      REC_screenPermissions: null,
      REC_microphonePermissions: null,
      REC_videoDeviceId: null, // What user selected in the dropdown
      REC_recordInterval: null,
      REC_devices: {
        video: [],
        audio: [],
      },
      REC_frameRate: null,
      REC_isRecording: false,
      REC_isPaused: false,
      REC_timings: {
        start: null,
        stop: null,
        duration: null,
      },
      REC_testStream: null,
      REC_displayMediaId: 'wochit-screen-capture',
      REC_videoDeviceLoading: false,
    };
  },
  computed: {
    REC_isDisplayRecording() {
      // Screen Capture mode
      return this.REC_videoDeviceId === this.REC_displayMediaId;
    },
  },
  created() {
    if (window.localStorage.videoSourceID) {
      this.REC_videoDeviceId = window.localStorage.videoSourceID;
    }
    window.navigator.mediaDevices.addEventListener(
      'devicechange',
      this.REC_getAvailbleStreamDevices
    );
  },
  beforeDestroy() {
    this.REC_stopRecording();
    this.REC_stopStream();
    this.$_mediaRecorder = null;
    window.navigator.mediaDevices.removeEventListener(
      'devicechange',
      this.REC_getAvailbleStreamDevices
    );
  },
  methods: {
    REC_verifySupported(supportVideo = true) {
      return new Promise((resolve, reject) => {
        if (this.REC_isDisplayRecording) {
          if (!window.navigator.mediaDevices.getDisplayMedia) {
            console.warn(
              'MediaRecorder for displayMedia is not supported from your browser.'
            );
            return reject(
              'MediaRecorder for displayMedia is not supported from your browser.'
            );
          }
          window.navigator.mediaDevices
            .getUserMedia({ video: false, audio: true }) // microphone
            .then((stream) => {
              stream.getTracks().forEach((t) => t.stop());
              stream = null;
              this.REC_microphonePermissions = true;
            })
            .catch((e) => {
              this.REC_microphonePermissions = false;
            })
            .then(() => {
              resolve(); // we allow recording flow even if no camera + mic detected
            });
        } else {
          if (
            !window.navigator.mediaDevices &&
            !window.navigator.mediaDevices.getUserMedia
          ) {
            this.REC_userMediaSupported = false;
            console.warn('Media Devices are not supported from your browser.');
            return reject('Media Devices are not supported from your browser.');
          }

          // video recorder on Safari is not currently supported
          // TODO: we could use https://github.com/CameraKit/webm-media-recorder
          if (!window.MediaRecorder) {
            this.REC_userMediaSupported = false;
            console.warn(
              'MediaRecorder for video is not supported from your browser.'
            );
            return reject(
              'MediaRecorder for video is not supported from your browser.'
            );
          }

          this.REC_userMediaSupported = true;

          let videoPromise;

          // camera
          if (supportVideo) {
            videoPromise = window.navigator.mediaDevices
              .getUserMedia({ video: true, audio: false })
              .then((stream) => {
                this.REC_cameraPermissions = true;
                stream.getTracks().forEach((t) => t.stop());
                stream = null;
              })
              .catch((e) => {
                this.REC_cameraPermissions = false;
              });
          } else {
            videoPromise = Promise.resolve();
          }

          // microphone
          videoPromise.then(() => {
            window.navigator.mediaDevices
              .getUserMedia({ video: false, audio: true })
              .then((stream) => {
                stream.getTracks().forEach((t) => t.stop());
                stream = null;
                this.REC_microphonePermissions = true;
              })
              .catch((e) => {
                this.REC_microphonePermissions = false;
              })
              .then(() => {
                resolve(); // we allow recording flow even if no camera + mic detected
              });
          });
        }
      });
    },

    async REC_getAvailableAudioStreamDevices() {
      const deviceInfos =
        await window.navigator.mediaDevices.enumerateDevices();
      this.REC_devices.audio = [];
      _.each(deviceInfos, (deviceInfo) => {
        const deviceId = deviceInfo.deviceId;
        if (!deviceId) {
          return;
        }
        if (deviceInfo.kind === 'audioinput') {
          this.REC_devices.audio.push({
            id: deviceId,
            label:
              deviceInfo.label ||
              'Microphone' + (this.REC_devices.audio.length + 1),
          });
        }
      });
    },

    async REC_getAvailableStreamDevices() {
      return new Promise((resolve, reject) => {
        window.navigator.mediaDevices
          .enumerateDevices()
          .then((deviceInfos) => {
            this.REC_devices.audio = [];
            this.REC_devices.video = [];
            _.each(deviceInfos, (deviceInfo) => {
              const deviceId = deviceInfo.deviceId;
              if (!deviceId) {
                return;
              }
              if (deviceInfo.kind === 'audioinput') {
                this.REC_devices.audio.push({
                  id: deviceId,
                  label:
                    deviceInfo.label ||
                    'Microphone' + (this.REC_devices.audio.length + 1),
                });
              } else if (deviceInfo.kind === 'videoinput') {
                this.REC_devices.video.push({
                  id: deviceId,
                  label:
                    deviceInfo.label ||
                    'Camera' + (this.REC_devices.video.length + 1),
                });
              }
            });
            this.REC_addDisplayDeviceToList();
            this.REC_addNoMicToList();
            resolve();
          })
          .catch((e) => {
            console.error(e);
            this.REC_addDisplayDeviceToList();
            reject('Failed getting available devices list.');
          });
      });
    },
    /**
     * Get the input stream based on constraints and emit the stream event
     * to the parent component so he can use it for processing or show a preview
     */
    async REC_getUserMedia() {
      let stream;
      if (this.REC_isDisplayRecording) {
        this.REC_screenPermissions = null;
        const displayConstraints = {
          video: {
            frameRate: { ideal: 30 },
            width: 3840,
            height: 2160,
          },
          cursor: 'motion',
          audio: true,
        }; // tbd
        stream = await window.navigator.mediaDevices.getDisplayMedia(
          displayConstraints
        );
        this.REC_screenPermissions = true;
      } else {
        const cameraConstraints = {
          video: {
            facingMode: 'user',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            aspectRatio: { ideal: 1.7777777778 },
            deviceId: this.REC_videoDeviceId
              ? this.REC_videoDeviceId
              : undefined,
            // frameRate: {min: 1, ideal: 30, max: 1000},
          },
          audio: false, // audio will be replaced later by AudioMixin's stream
        };
        stream = await window.navigator.mediaDevices.getUserMedia(
          cameraConstraints
        );
      }

      this.REC_stream = stream;
      this.REC_stream.getVideoTracks().forEach((t) => {
        const settings = t.getSettings();
        this.REC_videoDeviceId = this.REC_isDisplayRecording
          ? this.REC_displayMediaId
          : settings.deviceId;
        this.REC_frameRate = settings.frameRate;
      });
      return stream;
    },

    REC_changeDevice(deviceId) {
      this.REC_videoDeviceId = deviceId;
      if (!this.REC_isDisplayRecording) {
        window.localStorage.setItem('videoSourceID', deviceId);
      }
    },

    async REC_startStream() {
      this.REC_stopStream();

      if (this.REC_videoDeviceId) {
        const findVideoDevice = this.REC_devices.video.filter((vid) => {
          return vid.id === this.REC_videoDeviceId;
        });
        if (!findVideoDevice) {
          this.REC_videoDeviceId = null;
        }
      }

      return new Promise((resolve, reject) => {
        this.REC_videoDeviceLoading = true;
        this.REC_getUserMedia().then(
          (stream) => {
            this.REC_stream = stream;
            this.REC_videoDeviceLoading = false;
            resolve(this.REC_stream);
          },
          (e) => {
            if (this.REC_isDisplayRecording) {
              this.REC_screenPermissions = false;
            }
            this.REC_videoDeviceLoading = false;
            reject(e);
          }
        );
      });
    },

    REC_startRecording(videoMode = true) {
      return new Promise((resolve, reject) => {
        if (this.REC_isRecording) {
          return reject('Recording already in progress');
        }
        try {
          this.REC_prepareRecorder(videoMode);
          this.$_mediaRecorder.start();
          this.$_mediaRecorder.addEventListener('start', () => {
            console.log('recording start');
            this.REC_isRecording = true;
            this.REC_isPaused = false;
            this.REC_timings.start = Date.now();
            this.REC_timings.stop = null;
            this.REC_recordInterval = window.setInterval(() => {
              let duration = null;
              if (this.REC_timings.start) {
                duration =
                  ((this.REC_timings.end || Date.now()) -
                    this.REC_timings.start) /
                  1000;
              }
              this.REC_timings.duration = duration;
              resolve();
            }, 100);
          });
        } catch (e) {
          console.error(e);
          reject(e);
        }
      });
    },

    REC_stopRecording() {
      if (!this.REC_isRecording) return;
      window.clearInterval(this.REC_recordInterval);
      this.$_mediaRecorder.stop();
      this.REC_timings.stop = Date.now();
      this.REC_stopStream();
    },

    REC_pause() {
      if (!this.REC_isRecording) return;
      this.$_mediaRecorder.pause();
    },

    REC_resume() {
      if (!this.REC_isPaused) return;
      this.$_mediaRecorder.resume();
    },

    REC_stopStream() {
      if (this.REC_stream) {
        this.REC_stream.getTracks().forEach((t) => t.stop());
        this.REC_stream = null;
      }
    },

    REC_replaceAudioTrack(micAudioStream) {
      return new Promise((resolve, reject) => {
        if (!this.REC_stream) {
          return reject('Stream not available yet');
        }
        try {
          if (!this.REC_isDisplayRecording) {
            // we need to remove original audio from source
            this.REC_stream.getAudioTracks().forEach((t) => {
              this.REC_stream.removeTrack(t);
              t.stop();
            });
            this.REC_stream.addTrack(micAudioStream.getTracks()[0]);
          } else {
            // mediaStream doesn't support two audioTracks, we'll need to combine them
            // into one stream.
            const audioCtx = new AudioContext();
            const audioDestination = audioCtx.createMediaStreamDestination();

            if (micAudioStream.getTracks()[0]) {
              try {
                // micAudioStream might not have a valid track
                const sourceMic =
                  audioCtx.createMediaStreamSource(micAudioStream);
                sourceMic.connect(audioDestination);
              } catch (e) {
                console.log('REC_replaceAudioTrack: mic stream not available');
              }
            }

            if (this.REC_stream.getTracks()[0]) {
              try {
                // REC_stream might not have a valid track
                const sourceScreen = audioCtx.createMediaStreamSource(
                  this.REC_stream
                );
                sourceScreen.connect(audioDestination);
              } catch (e) {
                console.log(
                  'REC_replaceAudioTrack: REC_stream audio stream not available'
                );
              }
            }

            const outputStream = new MediaStream();
            outputStream.addTrack(this.REC_stream.getVideoTracks()[0]);

            if (audioDestination.stream.getAudioTracks()[0]) {
              outputStream.addTrack(
                audioDestination.stream.getAudioTracks()[0]
              );
            }

            this.REC_stream = outputStream;
          }

          resolve();
        } catch (e) {
          console.error(e);
          reject('Failed replacing audio track');
        }
      });
    },

    /**
     * Create a new media recorder with the user media stream
     * and set some event listeners to update component data
     * and emit events to the parent component
     */
    REC_prepareRecorder(videoMode = true) {
      const stream = videoMode ? this.REC_stream : this.AUD_stream;
      const mimeType = videoMode
        ? 'video/webm;codecs=h264'
        : 'audio/webm;codecs=opus';
      if (!stream) {
        return;
      }
      this.REC_chunks = [];
      this.$_mediaRecorder = new MediaRecorder(stream, {
        mimeType, // vp8, vp9, h264, mkv, opus/vorbis
        audioBitsPerSecond: 240000,
      });

      this.$_mediaRecorder.ignoreMutedMedia = true;

      this.$_mediaRecorder.addEventListener('resume', () => {
        this.REC_isRecording = true;
        this.REC_isPaused = false;
        console.log('recording resume');
      });

      this.$_mediaRecorder.addEventListener('pause', () => {
        this.REC_isPaused = true;
        console.log('recording pause');
      });

      // Collect the available data into chunks
      this.$_mediaRecorder.addEventListener(
        'dataavailable',
        (e) => {
          if (e.data && e.data.size > 0) {
            this.REC_chunks.push(e.data);
          }
        },
        true
      );

      // On recording stop get the data and emit the result
      // than clear all the recording chunks
      this.$_mediaRecorder.addEventListener(
        'stop',
        () => {
          const blobData = new Blob(this.REC_chunks, {
            type: videoMode ? 'video/webm' : 'audio/webm',
          });
          if (blobData.size > 0) {
            this.REC_injectBlobDurationMetadata(blobData);
          }
          this.REC_chunks = [];
          this.REC_isPaused = false;
          this.REC_isRecording = false;
        },
        true
      );
    },

    REC_readAsArrayBuffer(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsArrayBuffer(blob);
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.onerror = (ev) => {
          reject(ev.error);
        };
      });
    },

    REC_injectBlobDurationMetadata(blob) {
      const decoder = new Decoder();
      const reader = new Reader();
      reader.logging = false;
      reader.drop_default_duration = false;

      this.REC_readAsArrayBuffer(blob)
        .then((buffer) => {
          const elms = decoder.decode(buffer);
          elms.forEach((elm) => {
            // filter-out unknown EBML tags
            if (elm.type !== 'unknown') {
              reader.read(elm);
            }
          });
          reader.stop();
          this.REC_timings.duration = reader.duration / 1000;
          const refinedMetadataBuf = tools.makeMetadataSeekable(
            reader.metadatas,
            reader.duration,
            reader.cues
          );
          const body = buffer.slice(reader.metadataSize);

          const result = new Blob([refinedMetadataBuf, body], {
            type: blob.type,
          });
          this.$emit('videoResult', result);
          this.REC_chunks = [];
          this.REC_isPaused = false;
          this.REC_isRecording = false;
        })
        .catch((err) => {
          app.doctorController.report('RecorderMixin decode/encode error', err);
        });
    },

    REC_resetTimings() {
      this.REC_timings.start = null;
      this.REC_timings.stop = null;
      this.REC_timings.duration = null;
    },

    REC_addDisplayDeviceToList() {
      if (window.navigator.mediaDevices.getDisplayMedia) {
        this.REC_devices.video.push({
          id: this.REC_displayMediaId,
          label: 'Screen Recording',
        });
      }
    },

    REC_addNoMicToList() {
      if (this.REC_devices.audio.length) {
        this.REC_devices.audio.push({
          id: this.AUD_noMicId,
          label: 'No Microphone',
        });
      }
    },
  },
};
